/**
 * @fileoverview Display-only highlighting of unfilled report placeholders.
 *
 * The writer leaves "[to be confirmed]" (and leftover `{{merge fields}}`)
 * when structured matter data was missing. Headings can also keep leftover
 * `[Books and records source]`-style labels from the template title.
 *
 * Applied to the rendered DOM only — never written back to stored HTML.
 */

const UNFILLED_BODY_RE = /\[to be confirmed\]|\{\{[^{}\n]+\}\}/gi;
const UNFILLED_HEADING_RE =
  /\[to be confirmed\]|\{\{[^{}\n]+\}\}|\[[^\][]{3,80}\]/gi;

/**
 * True when a heading or body string still has an unfilled placeholder.
 *
 * @example
 * hasUnfilledPlaceholder("4.1 [Books and records source] / General Comments")
 */
export function hasUnfilledPlaceholder(text: string): boolean {
  UNFILLED_HEADING_RE.lastIndex = 0;
  return UNFILLED_HEADING_RE.test(text);
}

/**
 * Wrap unfilled placeholders in the rendered report so they stand out.
 * Headings also flag leftover `[Books and records source]`-style labels.
 */
export function flagUnfilledPlaceholders(root: HTMLElement): void {
  const scope = root.querySelector<HTMLElement>(".ProseMirror") ?? root;
  const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  let node: Node | null = walker.nextNode();
  while (node) {
    const el = node.parentElement;
    if (el && !el.closest("[data-report-unfilled]")) {
      const inHeading = !!el.closest("h1, h2, h3");
      const re = inHeading ? UNFILLED_HEADING_RE : UNFILLED_BODY_RE;
      re.lastIndex = 0;
      if (re.test(node.textContent ?? "")) nodes.push(node as Text);
    }
    node = walker.nextNode();
  }
  for (const textNode of nodes) {
    const inHeading = !!textNode.parentElement?.closest("h1, h2, h3");
    const re = inHeading ? UNFILLED_HEADING_RE : UNFILLED_BODY_RE;
    const text = textNode.textContent ?? "";
    const frag = document.createDocumentFragment();
    re.lastIndex = 0;
    const last = Array.from(text.matchAll(re)).reduce((idx, m) => {
      const start = m.index ?? 0;
      if (start > idx) frag.append(text.slice(idx, start));
      const span = document.createElement("span");
      span.setAttribute("data-report-unfilled", "");
      span.className = "report-unfilled";
      span.title = "Needs a value — not found in the matter data";
      span.textContent = m[0];
      frag.append(span);
      return start + m[0].length;
    }, 0);
    if (last < text.length) frag.append(text.slice(last));
    textNode.parentNode?.replaceChild(frag, textNode);
  }
}
